import { useEffect, useRef, useState } from "react";
import { Box, CardMedia, IconButton, Stack, Typography } from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

type Props = {
  nombre: string;
  principalUrl: string;
  galeriaUrl: string;
  token?: string;
  height: number;
  onPrincipalLoaded?: (url: string) => void;
};

const apiUrl = (path: string) => {
  const base = (process.env.REACT_APP_BASE_URL ?? "").replace(/\/$/, "");
  return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
};

export default function ProductoGaleria({ nombre, principalUrl, galeriaUrl, token, height, onPrincipalLoaded }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const principalLoadedRef = useRef(onPrincipalLoaded);
  principalLoadedRef.current = onPrincipalLoaded;
  const [visible, setVisible] = useState(false);
  const [adicionales, setAdicionales] = useState<string[]>([]);
  const [indice, setIndice] = useState(0);
  const [principal, setPrincipal] = useState<string>();
  const [actual, setActual] = useState<string>();
  const [fallida, setFallida] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
    }, { rootMargin: "260px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const controller = new AbortController();
    fetch(apiUrl(galeriaUrl), { signal: controller.signal, headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.ok ? r.json() : [])
      .then((urls: string[]) => { if (!controller.signal.aborted) setAdicionales(Array.isArray(urls) ? urls : []); })
      .catch(() => {});
    return () => controller.abort();
  }, [visible, galeriaUrl, token]);

  useEffect(() => {
    if (!visible || !principalUrl) return;
    if (!token) { setPrincipal(apiUrl(principalUrl)); return; }
    const controller = new AbortController();
    let objectUrl: string | undefined;
    fetch(apiUrl(principalUrl), { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.blob() : undefined)
      .then(blob => { if (blob && !controller.signal.aborted) {
        objectUrl = URL.createObjectURL(blob);
        setPrincipal(objectUrl);
        principalLoadedRef.current?.(objectUrl);
      } })
      .catch(() => {});
    return () => { controller.abort(); };
  }, [visible, principalUrl, token]);

  useEffect(() => {
    setActual(undefined);
    setFallida(false);
    if (!visible || indice === 0) return;
    const path = adicionales[indice - 1];
    if (!path) return;
    if (!token) { setActual(apiUrl(path)); return; }
    const controller = new AbortController();
    let objectUrl: string | undefined;
    fetch(apiUrl(path), { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.blob() : undefined)
      .then(blob => { if (blob && !controller.signal.aborted) {
        objectUrl = URL.createObjectURL(blob);
        setActual(objectUrl);
      } else if (!controller.signal.aborted) setFallida(true); })
      .catch(() => { if (!controller.signal.aborted) setFallida(true); });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [visible, indice, adicionales, token]);

  const total = adicionales.length + 1;
  const imagen = indice === 0 ? principal : actual;
  const mover = (paso: number) => setIndice(i => (i + paso + total) % total);
  return <Box ref={ref} sx={{ position: "relative", bgcolor: "action.hover" }}>
    {imagen && !fallida ? <CardMedia component="img" height={String(height)} image={imagen} alt={`${nombre}, foto ${indice + 1} de ${total}`}
      onError={() => setFallida(true)} sx={{ objectFit: "contain", p: 1 }} /> :
      <Box sx={{ height, display: "grid", placeItems: "center", color: "text.secondary", fontSize: 42, fontWeight: 700 }}>
        {fallida ? "Imagen no disponible" : nombre.slice(0, 1).toUpperCase()}
      </Box>}
    {total > 1 && <>
      <IconButton aria-label={`Foto anterior de ${nombre}`} onClick={() => mover(-1)} size="small"
        sx={{ position: "absolute", left: 4, top: "42%", bgcolor: "background.paper" }}><NavigateBeforeIcon /></IconButton>
      <IconButton aria-label={`Foto siguiente de ${nombre}`} onClick={() => mover(1)} size="small"
        sx={{ position: "absolute", right: 4, top: "42%", bgcolor: "background.paper" }}><NavigateNextIcon /></IconButton>
      <Stack direction="row" justifyContent="center" spacing={0.5} sx={{ position: "absolute", bottom: 4, width: "100%" }}>
        {Array.from({ length: total }, (_, i) => <IconButton key={i} size="small" onClick={() => setIndice(i)}
          aria-label={`Mostrar foto ${i + 1} de ${nombre}`} aria-current={i === indice ? "true" : undefined}
          sx={{ p: 0.5, bgcolor: "background.paper" }}><Typography fontSize={11}>{i === indice ? "●" : "○"}</Typography></IconButton>)}
      </Stack>
    </>}
  </Box>;
}
